import { inject, provide, reactive, ref, type Ref } from 'vue';
import { useRouter, type RouteLocationNormalizedLoaded } from 'vue-router';
import { Contact, Lead } from '@keyvue/one-api-client';
import { OrderAPI, type Order } from '@keyvue/one-api-client-legacy';
import useClient, { type ReactiveCollection } from '@composables/useClient';
import useEventBus from '@composables/useEventBus';
import createSharedLoad from '@/utils/createSharedLoad';

export interface ContactPageData {
    contactId: string;
    contact: () => Promise<Contact>;
    /**
     * The page's contact for a component that opens later, like the lead side sheet. When the route
     * changed since the contact last loaded, it is reloaded in the background and updated in place, as
     * the sheet used to load its own fresh copy. Within the same route (a direct link to a lead) it is
     * just shared.
     */
    freshContact: () => Promise<Contact>;
    leadCollection: () => Promise<ReactiveCollection<Lead>>;
    reloadLeadCollection: () => Promise<void>;
    orders: () => Promise<Ref<Order[]>>;
}

/**
 * The contact, its leads and its orders: the data several parts of the contact page need.
 *
 * Each item is loaded when a component first asks for it and shared from then on. After a
 * 'resource-manipulated' event it is reloaded once, in place, so every component shows the same
 * data; deleted leads and orders reload the same way. These are the events the components
 * reacted to before they shared this data.
 */
function createContactPageData(contactId: string): ContactPageData {
    const { client, getReactiveCollection } = useClient();
    const eventBus = useEventBus();
    const router = useRouter();

    // The route the contact was last loaded on. Failed and aborted navigations don't change it.
    let contactLoadedOnRoute: RouteLocationNormalizedLoaded | null = null;

    const loadContact = async () => {
        const loaded = await client.get(Contact, contactId);
        contactLoadedOnRoute = router.currentRoute.value;

        return loaded;
    };
    const loadOrders = () => OrderAPI.getAll({ 'referral.contact': contactId });

    const contact = createSharedLoad(
        async () => reactive(await loadContact()) as Contact,
        async (current) => { Object.assign(current, await loadContact()); },
    );
    const leadCollection = createSharedLoad(
        () => getReactiveCollection(Lead, { primaryContact: contactId }),
        (current) => current.load(),
    );
    const orders = createSharedLoad(
        async () => ref(await loadOrders()) as Ref<Order[]>,
        async (current) => {
            // eslint-disable-next-line no-param-reassign
            current.value = await loadOrders();
        },
    );

    const reloadLeadsAndOrders = async (params: string) => {
        if (params === 'Lead') {
            await leadCollection.reload();
        }

        if (params === 'Order') {
            await orders.reload();
        }
    };

    eventBus.subscribe<string>('resource-manipulated', async (params) => {
        if (params === 'Contact') {
            await contact.reload();
        }

        await reloadLeadsAndOrders(params);
    });
    eventBus.subscribe<string>('resource-deleted', reloadLeadsAndOrders);

    return {
        contactId,
        contact: contact.get,
        freshContact: () => {
            if (contactLoadedOnRoute && contactLoadedOnRoute !== router.currentRoute.value) {
                // Not awaited: the card shows the page's contact now and the reload updates it in place.
                contact.reload();
            }

            return contact.get();
        },
        leadCollection: leadCollection.get,
        reloadLeadCollection: leadCollection.reload,
        orders: orders.get,
    };
}

/**
 * Shares the contact page's data with every component below the calling one (ContactDetail).
 * Without it, the cards, the lead side sheet and the create action/comment menus loaded their own
 * copies: the contact about six times and the lead list four times per lead view.
 */
export function provideContactPageData(contactId: string): void {
    provide('contactPageData', createContactPageData(contactId));
}

/**
 * The contact page's shared data for this contact. Outside the contact page, or for another
 * contact, the component gets its own copy, loaded and reloaded the same way.
 */
export default function useContactPageData(contactId: string): ContactPageData {
    const provided = inject<ContactPageData | null>('contactPageData', null);

    if (provided && provided.contactId.toLowerCase() === contactId.toLowerCase()) {
        return provided;
    }

    return createContactPageData(contactId);
}
